import { zodResolver } from '@hookform/resolvers/zod';
import { useDevLogin, useLogin } from '@packages/cognito-auth/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { FormWrapper } from '@/components/FormWrapper';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { DEV_AUTH_ENABLED, devLoginRoles } from '@/features/auth/devAuth';
import { isSquareSignIn, SquareSignIn } from '@/features/auth/SquareSignIn';
import { track } from '@/lib/analytics';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';

export const SignIn = () => {
  const showUsernameForm = new URLSearchParams(window.location.search).get('u') === '1';

  return (
    <div className="h-screen flex justify-center items-center bg-neutral-800">
      {isSquareSignIn() ? <SquareSignIn /> : showUsernameForm ? <LoginWithUsername /> : <OtpForm />}
      {DEV_AUTH_ENABLED && <DevLogin />}
    </div>
  );
};

//
// DevLogin — bypasses Cognito with a locally signed token (see packages/cognito-auth).
// Compiled out of builds that don't set VITE_DEV_AUTH_BYPASS.
//
const DevLogin = () => {
  const { loginAs, pendingRole, loginError } = useDevLogin(devLoginRoles);

  // A single warm-tinted strip parked in the bottom-left corner, clear of the
  // login card: the tint reads instantly as a non-production affordance, and a
  // two-up segmented control keeps it to one row.
  return (
    <div className="fixed bottom-4 left-4 flex w-[360px] flex-col gap-2">
      <div className="flex items-center gap-3 rounded-xl border border-[#33312c] bg-[#1f1f1d] px-3 py-2.5">
        <div className="flex flex-col gap-0.5 font-mono text-[11px]">
          <span className="uppercase tracking-[0.1em] text-[#c8a24a]">Dev</span>
          <span className="text-[#77716a]">log in as</span>
        </div>
        <div className="flex flex-1 gap-1 rounded-[9px] bg-[#171715] p-1">
          {devLoginRoles.map((role) => (
            <button
              className="h-9 flex-1 whitespace-nowrap rounded-md bg-[#2b2b29] text-sm text-[#e4e4e1] transition-colors hover:bg-[#38382f] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={pendingRole !== null}
              key={role}
              onClick={() => loginAs(role)}
              type="button"
            >
              {devLoginLabels[role]}
            </button>
          ))}
        </div>
      </div>
      {loginError && <div className="text-red-500 dark:text-red-400 font-medium">{loginError}</div>}
    </div>
  );
};

const devLoginLabels: Record<(typeof devLoginRoles)[number], string> = {
  restaurantUser: 'Restaurant user',
  superAdmin: 'Super admin',
};

const usernameStrictSchema = z.object({
  password: z.string(),
  username: z.string(),
});
type UsernameForm = z.output<typeof usernameStrictSchema>;

//
// LoginWithUsername
//
const LoginWithUsername = () => {
  const form = useForm({
    defaultValues: { password: '', username: '' },
    resolver: zodResolver(usernameStrictSchema),
  });

  const { userVisibleError, signInWithPassword } = useLogin();

  const onValidSubmit = async ({ password, username }: UsernameForm) => {
    try {
      await signInWithPassword(username, password);
      track('clicked_sign_in', { signup_method: 'username' });
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <Card className="w-[320px]">
      <CardHeader>
        <CardTitle className="text-xl">Login</CardTitle>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            className={'grid gap-6'}
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
          >
            {/* Username */}
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input {...field} required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="on" required type="password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ActionButton
              disabled={form.formState.isSubmitting}
              loading={form.formState.isSubmitting}
              type="submit"
            >
              Sign in
            </ActionButton>
          </form>
        </FormWrapper>
      </CardContent>

      {userVisibleError && (
        <CardFooter>
          <div className={'text-red-500 dark:text-red-400 font-medium'}>{userVisibleError}</div>
        </CardFooter>
      )}
    </Card>
  );
};

//
// OtpForm
//

const OtpForm = () => {
  const {
    mobile,
    code,
    isLoading,
    showCodeInput,
    userVisibleError,
    handleOnMobileChange,
    handleCodeChange,
    sendCode,
    verifyCode,
  } = useLogin();

  const handleSignIn = async () => {
    track('clicked_sign_in', { signup_method: 'OTP' });
    await verifyCode(code);
  };

  return (
    <Card className="w-[320px]">
      <CardHeader>
        <CardTitle className="text-xl">Login</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6">
        {/* Mobile */}
        {!showCodeInput && (
          <>
            <div className="grid gap-2">
              <Label>Mobile number</Label>
              <Input
                disabled={showCodeInput}
                maxLength={10}
                onChange={(e) => handleOnMobileChange(e.target.value)}
                required
                value={mobile}
              />
            </div>

            <ActionButton
              disabled={isLoading || showCodeInput}
              loading={isLoading}
              onClick={() => sendCode(mobile)}
            >
              Send verification code
            </ActionButton>
          </>
        )}

        {/* Code */}
        {showCodeInput && (
          <>
            <div className="grid gap-2">
              <Label>Code</Label>
              <InputOTP
                autoFocus={true}
                maxLength={6}
                onChange={(value) => handleCodeChange(value)}
                value={code}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <ActionButton disabled={isLoading} loading={isLoading} onClick={handleSignIn}>
              Sign in
            </ActionButton>
          </>
        )}
      </CardContent>
      <CardFooter>
        <div className={'text-red-500 dark:text-red-400'}>{userVisibleError}</div>
      </CardFooter>
    </Card>
  );
};
